import React, { useEffect, useState } from 'react';
import assetAuditService from 'api/assetAuditService';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useDispatch } from 'react-redux';
import Table from '../../componentsV2/Table/Table';
import { dateTimeFormatter, findElementByPrompt } from 'utils/utils';
import PdfPreview from 'componentsV2/PDFPreview/PdfPreview';
import './AssetAudit.scss';
import AssetAuditModal from './AssetAuditModal';
import { CorePagination } from '@komodo-design-system/komodo-components-react';
import AlertBox from 'componentsV2/SharedAlertBox/AlertBox/AlertBox';
const eventType = {
  'UPDATE': 'Updated',
  'ADD': 'Added',
  'CHECKIN': 'Checked In',
  'PUBLISHED': 'Published',
  'PUBLISHINPROGRESS': 'Publish In Progress',
  'UNPUBLISHED': 'Unpublished',
  'UNPUBLISHINPROGRESS': 'Unpublish In Progress',
  'DELETE': 'Deleted',
  'DRAFT': 'Drafted',
  'NEW':'Created'
};

const AssetAudit = ({assetId, assetDetails}:{assetId: string | undefined, assetDetails: any}) => {
  const [auditDetails, setAuditDetails] = useState({
    asset_data: [],
    total_count: 0
  });
  const dispatch: ThunkDispatch<any, any, AnyAction> = useDispatch();
  const [leftWidth, setLeftWidth] = useState(48); // Initial width of the left section (percentage)
  const [isDragging, setIsDragging] = useState(false); // State to track dragging
  const [isOpen,setIsOpen]=useState(false);
  const [currentAsset, setCurrentAsset] = useState<any>(null);
  const [previousAsset, setPreviousAsset] = useState<any>(null);
  const MAX_LEFT_WIDTH = 80; // Maximum width for the left section (percentage)
  const MIN_LEFT_WIDTH = 53; // Minimum width for the left section (percentage)
  const [paginationValues, setPaginationValues] = useState({
    pageNumber: 1,
    rpp: 10
  });
  const { pageNumber, rpp } = paginationValues;
  const [alert, setAlert] = useState({ isModalOpen: false, message: ''});
  // Handle mouse down on the resizer
  const handleMouseDown = () => {
    setIsDragging(true);
  };

  

  // Handle mouse move to resize sections
  const handleMouseMove = (e: any) => {
    if (!isDragging) {
      return; // If not dragging, do nothing
    }

    // Calculate the new width of the left section
    const newLeftWidth = (e.clientX / window.innerWidth) * 100;

    // Restrict the width between MIN_LEFT_WIDTH and MAX_LEFT_WIDTH
    if (newLeftWidth >= MIN_LEFT_WIDTH && newLeftWidth <= MAX_LEFT_WIDTH) {
      setLeftWidth(newLeftWidth); // Update the width
    }
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add event listeners for mouse move and mouse up
  React.useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    }
    // remove the page scrollbar
    // document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

   
  const originalFilePath = findElementByPrompt(assetDetails, 'Original File Path'); // Replace with your logic to determine visibility
  // Define table columns with specified accessors
  const handleCellClick = (value: any, accessor: any, row: any,index:number) => {
    // call assetAuditDetails api to get current and previous asset details
    dispatch({ type: 'LOADING SUCCESS', payload: true });
    assetAuditService.getAssetAuditDetails(assetId,row.id).then(response => {
      // fail if response is not ok
      if (!response.ok) {
        setAlert({ isModalOpen: true, message: 'Failed to fetch asset audit details. Please try again later.' });
      }
      return response.json();
    }).then(data => {
      if(!data || data.length===0 || data?.message){
        setAlert({ isModalOpen: true, message: data?.message || 'No audit details found for this record.' });
        return;
      }
      setCurrentAsset(data[0] || null);
      setPreviousAsset(data[1] || null);
      setIsOpen(true);
    }).finally(() => {
      dispatch({ type: 'LOADING SUCCESS', payload: false });
    });

  };
  const columns = [
    {
      header: 'Event',
      accessor: 'action',
      sortable: true,
      cell: (row: any) => <span>{eventType[row.action?.toUpperCase() as keyof typeof eventType] || 'N/A'}</span>,
      onCellClick: (value: any, accessor: any, row: any,index:number) =>row.action?.toUpperCase()==='UPDATE' && handleCellClick(value,accessor,row,index),
      cellClassName: (row: any) =>
        row.action?.toUpperCase() !== 'UPDATE' ? 'disable-link' : ''
    },
    {
      header: 'User',
      accessor: 'modified_by',
      sortable: true,
      cell: (row: any) => <span>{row.modified_by || 'N/A'}</span>
    },
    {
      header: 'Modified Date',
      accessor: 'modified_dt',
      sortable: true,
      cell: (row: any) => {
        const date = row.modified_dt;
        if (!date) {
          return <span>N/A</span>;
        }
        try {
          return <span>{dateTimeFormatter(date)}</span>;
        } catch {
          return <span>{date}</span>;
        }
      }
    }
  ];

  useEffect(() => {
    const fetchAuditDetails = async () => {
      if (assetId) {
        dispatch({ type: 'LOADING SUCCESS', payload: true });  
        const response = await assetAuditService.getAssetAudits(assetId, pageNumber, rpp);
        // fail if response is not ok
        if (!response.ok) {
          setAlert({ isModalOpen: true, message: 'Failed to fetch asset audits. Please try again later.' });
        }
        response.json().then(data => {
          setAuditDetails(data);
          dispatch({ type: 'LOADING SUCCESS', payload: false });
        });
      }
    };
    fetchAuditDetails();
  }, [assetId, dispatch, pageNumber, paginationValues, rpp]);

  useEffect(() => {
    document.title = 'Asset Audit';
  }, []);

  const handleActivePageChange = (activePage: number) => {
    setPaginationValues({ ...paginationValues, pageNumber: activePage });
  };
  
  return (
    <div className="split-screen-asset-audit">
      {originalFilePath?.value?.id &&
                <>
                  <div className="left-section" style={{ width: `${leftWidth}%` }}>
                    <PdfPreview path={originalFilePath?.value?.id} />
                  </div>
                  {/* Resizer */}
                  <div className="resizer" onMouseDown={handleMouseDown}></div>
                </>
      }
      <div className="right-section" style={{ width: !originalFilePath?.value?.id ? '100%' : `${100 - leftWidth}%` ,margin:'8px'}}>

        {auditDetails && Array.isArray(auditDetails?.asset_data) && auditDetails?.asset_data.length > 0 && ( <>
          <Table 
            data={auditDetails?.asset_data || []} 
            columns={columns}
            isExpandable={false}
          />
          {auditDetails?.total_count > paginationValues.rpp && (
            <CorePagination
              variants={{ dropdown: true, indicator: false, goto: false }}
              totalresults={auditDetails?.total_count}
              activepage={pageNumber || 1}
              activeresultsperpage={paginationValues.rpp}
              onActivePageChange={(event) => handleActivePageChange(event.detail.activePage)}
              onResultsPerPageChange={(event) => setPaginationValues({ ...paginationValues, rpp: event.detail })}
              resultsperpage={[10, 20, 30]}
            />
          )} </>
        ) }
      </div>
      {isOpen && currentAsset && previousAsset && (
        <AssetAuditModal isOpen={isOpen} onClose={() => setIsOpen(false)} currentAsset={currentAsset} previousAsset={previousAsset} />
      )}
      {alert.isModalOpen && <AlertBox isModalOpen={alert.isModalOpen} message={alert.message} bgClose={false} handleClose={() => setAlert({ isModalOpen: false, message: ''})} />}
    </div>
  );
};

export default AssetAudit;